import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router";
import { useAuthStore } from "@/stores/auth.module";
import type { CustomRoute } from "@/interfaces";
import { MODULE_THEMES, PLANNER_ROUTES } from "@/consts";
import { usePlannerStore } from "@/stores/planner.module";

export const routes: CustomRoute[] = [
  {
    name: "planner/edit-task",
    // There appears to be a bug, probably in pathToRegexp, in that the expression
    // \\d+\\|?\\d* will correctly match our 123|456 ids and convert them to param,
    // but will not insert it into the URL, giving problems with back/forward buttons.
    // For now we will leave the id param with no pattern and do the validation elsewhere.
    path: "/planner/edit-task/:id?",
    component: () => import("@/views/planner/EditTask.vue"),
  },
  {
    path: "/planner",
    // route level code-splitting
    // this generates a separate chunk (about.[hash].ts) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import("@/views/Planner.vue"),
    children: [
      {
        name: "planner/day-view",
        path: "",
        component: () => import("@/views/planner/DayView.vue"),
        meta: {
          ...MODULE_THEMES.planner,
          displayName: "global.planner",
          storeName: undefined,
        },
      },
      {
        name: "planner/day-view/specific",
        path: "day-view",
        component: () => import("@/views/planner/DayView.vue"),
        meta: {
          ...MODULE_THEMES.planner,
          displayName: "global.planner",
          storeName: undefined,
        },
      },
      {
        name: "planner/search",
        path: "search",
        component: () => import("@/views/planner/SearchResults.vue"),
        meta: {
          ...MODULE_THEMES.planner,
          displayName: "global.planner",
          storeName: undefined,
        },
      },
      {
        name: "planner/overdue/specific",
        path: "overdue",
        component: () => import("@/views/planner/Overdue.vue"),
        meta: {
          ...MODULE_THEMES.planner,
          displayName: "global.planner",
          storeName: undefined,
        },
      },
      {
        name: "planner/week-view/specific",
        path: "week-view",
        component: () => import("@/views/planner/WeekView.vue"),
        meta: {
          ...MODULE_THEMES.planner,
          displayName: "global.planner",
          storeName: undefined,
        },
      },
    ],
  },
  {
    name: "scripts",
    path: "/scripts",
    component: () => import("@/views/scripts/Scripts.vue"),
  },
  {
    name: "screen-configs",
    path: "/screen-configs",
    component: () => import("@/views/screenAndAppConfig/ScreenConfig.vue"),
  },
  {
    name: "app-configs",
    path: "/app-configs",
    component: () => import("@/views/screenAndAppConfig/AppConfig.vue"),
  },
  {
    name: "home",
    path: "/",
    component: () => import("@/views/Home.vue"),
    meta: {
      svg: "fas-rectangle-list",
      color: "#000",
      displayName: "global.dashboard",
    },
  },
  {
    name: "callback",
    path: "/callback",
    component: () => import("@/views/Callback.vue"),
  },
  {
    name: "loginError",
    path: "/login-error",
    component: () => import("@/views/LoginError.vue"),
  },
  {
    name: "news",
    path: "/news",
    component: () => import("@/views/news/Layout.vue"),
    redirect: { name: "news-main" },
    children: [
      {
        name: "news-main",
        path: "main",
        meta: {
          ...MODULE_THEMES.news,
          displayName: "global.news",
          storeName: "NEWS",
        },
        component: () => import("@/views/news/News.vue"),
      },
      {
        name: "news-categories",
        path: "categories",
        meta: {
          ...MODULE_THEMES.news,
          displayName: "global.categories",
          storeName: "NEWS_CATEGORIES",
          descriptionKey: "news.category_description",
        },
        component: () => import("@/views/shared/categories/Categories.vue"),
      },
    ],
  },
  {
    name: "activities",
    path: "/activities",
    component: () => import("@/views/activities/Layout.vue"),
    redirect: { name: "activities-main" },
    children: [
      {
        name: "activities-main",
        path: "main",
        meta: {
          ...MODULE_THEMES.activities,
          displayName: "global.activities",
          storeName: "ACTIVITIES",
        },
        component: () => import("@/views/activities/Activities.vue"),
      },
      {
        name: "activities-categories",
        path: "categories",
        meta: {
          ...MODULE_THEMES.activities,
          displayName: "global.categories",
          storeName: "ACTIVITIES_CATEGORIES",
          descriptionKey: "activity.category_description",
        },
        component: () => import("@/views/shared/categories/Categories.vue"),
      },
    ],
  },
  {
    name: "employees",
    path: "/employees",
    component: () => import("@/views/employees/Layout.vue"),
    redirect: { name: "employees-main" },
    children: [
      {
        name: "employees-main",
        path: "main",
        meta: {
          ...MODULE_THEMES.employees,
          displayName: "global.employees",
          storeName: "EMPLOYEES",
        },
        component: () => import("@/views/employees/Employees.vue"),
      },
      {
        name: "employees-titles",
        path: "titles",
        meta: {
          ...MODULE_THEMES.employees,
          displayName: "global.titles",
          storeName: "EMPLOYEES_TITLES",
          descriptionKey: "ourhome.employees.titles_description",
        },
        component: () => import("@/views/employees/Titles.vue"),
      },
    ],
  },
  {
    name: "ourHome",
    path: "/our-home/employees",
    component: () => import("@/views/BaseOldManager.vue"),
    meta: {
      ...MODULE_THEMES.employees,
      displayName: "global.ourhome",
    },
  },
  {
    name: "meetings",
    path: "/meeting/meetings",
    component: () => import("@/views/meetings/Layout.vue"),
    redirect: { name: "meetings-main" },
    children: [
      {
        name: "meetings-main",
        path: "main",
        meta: {
          ...MODULE_THEMES.meetings,
          displayName: "global.meetings",
          storeName: "MEETINGS",
        },
        component: () => import("@/views/meetings/Meetings.vue"),
      },
      {
        name: "meetings-agenda-point-templates",
        path: "agenda-point-templates",
        meta: {
          ...MODULE_THEMES.meetings,
          displayName: "global.agenda",
          storeName: "MEETINGS_AGENDA_POINTS",
          descriptionKey: "meeting.agendaItems.description",
        },
        component: () => import("@/views/meetings/MeetingAgendaPoints.vue"),
      },
      {
        name: "meetings-categories",
        path: "categories",
        meta: {
          ...MODULE_THEMES.meetings,
          displayName: "global.categories",
          storeName: "MEETINGS_CATEGORIES",
          descriptionKey: "meeting.category_description",
        },
        component: () => import("@/views/shared/categories/Categories.vue"),
      },
    ],
  },
  {
    name: "workSchedule",
    path: "/shift-plan/schedule-plan",
    component: () => import("@/views/BaseOldManager.vue"),
    meta: {
      ...MODULE_THEMES.workSchedule,
      displayName: "global.scheduleplan",
    },
  },
  {
    name: "booking",
    path: "/booking/bookable-bookings",
    component: () => import("@/views/Booking.vue"),
    meta: {
      ...MODULE_THEMES.booking,
      displayName: "global.booking",
    },
  },
  {
    name: "meals-new",
    path: "/meals",
    component: () => import("@/views/meals/Layout.vue"),
    redirect: { name: "meals-main" },
    children: [
      {
        name: "meals-main",
        path: "main",
        meta: {
          ...MODULE_THEMES.meals,
          displayName: "global.mealplan",
          storeName: "MEALS",
        },
        component: () => import("@/views/meals/Meals.vue"),
      },
      {
        name: "meals-dishes",
        path: "dishes",
        meta: {
          ...MODULE_THEMES.meals,
          displayName: "meal.dishes",
          storeName: "MEALS_DISHES",
        },
        component: () => import("@/views/meals/MealDishes.vue"),
      },
      {
        name: "meals-export",
        path: "export",
        meta: {
          ...MODULE_THEMES.meals,
          displayName: "meal.data_download",
          storeName: "MEALS",
        },
        component: () => import("@/views/meals/MealExport.vue"),
      },
    ],
  },
  {
    name: "media",
    path: "/media/galleries",
    component: () => import("@/views/BaseOldManager.vue"),
    meta: {
      ...MODULE_THEMES.media,
      displayName: "global.media",
    },
  },
  {
    name: "groups",
    path: "/groups",
    component: () => import("@/views/BaseOldManager.vue"),
    meta: {
      ...MODULE_THEMES.groups,
      displayName: "global.groups",
    },
  },
  {
    name: "admin",
    path: "/admin",
    component: () => import("@/views/admin/Main.vue"),
    redirect: { name: "core-news" },
  },
  {
    name: "core-news",
    path: "/admin/core-news",
    meta: {
      ...MODULE_THEMES.admin,
      displayName: "global.ibg_news",
      storeName: "CORE_NEWS",
      isAdmin: true,
    },
    component: () => import("@/views/coreNews/Layout.vue"),
    redirect: { name: "core-main" },
    children: [
      {
        name: "core-main",
        path: "main",
        meta: {
          ...MODULE_THEMES.admin,
          displayName: "global.ibg_news",
          storeName: "CORE_NEWS",
        },
        component: () => import("@/views/coreNews/CoreNews.vue"),
      },
      {
        name: "authors",
        path: "authors",
        meta: {
          ...MODULE_THEMES.admin,
          displayName: "news.authors",
          storeName: "CORE_NEWS",
        },
        component: () => import("@/views/coreNews/Authors.vue"),
      },
    ],
  },
  {
    name: "support",
    path: "/support",
    component: () => import("@/views/support/Layout.vue"),
    meta: {
      svg: "fas-circle-question",
      color: "#151515",
      displayName: "global.support",
    },
  },
  {
    name: "coreNewsList",
    path: "/core-news-list",
    component: () => import("@/views/coreNewsReader/Layout.vue"),
  },
  {
    name: "coreNewsDetail",
    path: "/core-news-detail/:id?",
    component: () => import("@/views/coreNewsReader/Detail.vue"),
  },
  {
    name: "relativeProfileCreation",
    path: "/relative-profile-creation",
    component: () => import("@/views/RelativeProfileCreationView.vue"),
  },
  {
    path: "/:pathMatch(.*)",
    name: "not-found",
    component: () => import("@/views/NotFound.vue"),
  },
];

const devRoutes = [
  {
    name: "sandbox",
    path: "/sandbox",
    component: () => import("@/views/sandbox/Sandbox.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    ...(routes as RouteRecordRaw[]),
    ...(import.meta.env.DEV ? devRoutes : []),
  ],
});

router.beforeEach(async (to) => {
  const authStore = useAuthStore();

  if (
    (to.name === "callback" || to.name === "loginError") &&
    authStore.isAuthenticated === true
  ) {
    return "/";
  }

  if (
    !window?.$features?.iframe &&
    authStore.isAuthenticated === false &&
    to.name !== "callback" &&
    to.name !== "loginError" &&
    to.name !== "relativeProfileCreation"
  ) {
    await authStore.redirectLogin();
    return false;
  }
});

router.afterEach((to) => {
  // If we're leaving the planner, reset filters so they apply again when coming back
  if (PLANNER_ROUTES.includes(to.name?.toString() || "") === false) {
    const plannerStore = usePlannerStore();
    plannerStore.isCustomFiltersSet = false;
  }
});

export default router;
